import { ConfirmModal } from '@affine/component';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useCallback, useState } from 'react';

import type { StealInfo } from './sync';

const MAX_DISPLAY_LENGTH = 50;

function truncate(
  text: string,
  maxLength: number = MAX_DISPLAY_LENGTH
): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

export interface StealDialogProps {
  stealInfo: StealInfo;
  newOwnerDisplay: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Dialog zur Bestaetigung eines Relation-Transfers.
 * Uses AFFiNE's native ConfirmModal for consistent styling.
 */
export function StealDialog({
  stealInfo,
  newOwnerDisplay,
  onConfirm,
  onCancel,
}: StealDialogProps) {
  return (
    <ConfirmModal
      open={true}
      onOpenChange={(open: boolean) => {
        if (!open) onCancel();
      }}
      title="Transfer Relation?"
      description={
        <span style={{ display: 'block' }}>
          <span
            style={{ display: 'block', margin: '0 0 12px', lineHeight: 1.5 }}
          >
            &quot;<strong>{truncate(stealInfo.targetRowDisplay)}</strong>&quot;
            is currently linked to:
          </span>

          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 14px',
              backgroundColor: cssVarV2('layer/background/secondary'),
              borderRadius: 8,
              marginBottom: 12,
              fontSize: 14,
            }}
          >
            <span>{truncate(stealInfo.currentOwnerDisplay)}</span>
          </span>

          <span
            style={{
              display: 'block',
              margin: 0,
              fontSize: 13,
              lineHeight: 1.5,
              color: cssVarV2('text/secondary'),
            }}
          >
            Transferring to &quot;
            <strong>{truncate(newOwnerDisplay)}</strong>&quot; will remove this
            link from {truncate(stealInfo.currentOwnerDisplay)}.
          </span>
        </span>
      }
      confirmText="Transfer Anyway"
      cancelText="Cancel"
      confirmButtonOptions={{ variant: 'error' }}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

/**
 * Hook zum Anzeigen des Steal-Dialogs
 * Gibt eine Funktion zurueck die ein Promise<boolean> returned
 */
export function useStealDialog() {
  const [dialogState, setDialogState] = useState<{
    stealInfo: StealInfo;
    newOwnerDisplay: string;
    resolve: (value: boolean) => void;
  } | null>(null);

  const showStealDialog = useCallback(
    (stealInfo: StealInfo, newOwnerDisplay: string): Promise<boolean> => {
      return new Promise(resolve => {
        setDialogState({
          stealInfo,
          newOwnerDisplay,
          resolve,
        });
      });
    },
    []
  );

  const handleConfirm = useCallback(() => {
    if (dialogState) {
      dialogState.resolve(true);
      setDialogState(null);
    }
  }, [dialogState]);

  const handleCancel = useCallback(() => {
    if (dialogState) {
      dialogState.resolve(false);
      setDialogState(null);
    }
  }, [dialogState]);

  const DialogComponent = dialogState ? (
    <StealDialog
      stealInfo={dialogState.stealInfo}
      newOwnerDisplay={dialogState.newOwnerDisplay}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  ) : null;

  return {
    showStealDialog,
    DialogComponent,
  };
}
